/**
 * NoteEditTab.jsx — Muhasebe FAZ 4.8, "Senet Düzenleme" iç sekmesi.
 *
 * Uçlar:
 *   PUT    /admin/promissory-notes/{note_id}   — düzenleme + TAHSİLAT
 *   DELETE /admin/promissory-notes/{note_id}   — yalnız `active` senette
 *
 * Backend kısıtlarının ekrandaki karşılığı:
 *   - `status` bu uçtan YALNIZCA "collected" olabilir; iptal/parçala/
 *     birleştir/icra kendi sekmelerinde. Bu yüzden burada "durum" seçici YOK,
 *     yalnızca "tahsil edildi olarak işaretle" kutusu var.
 *   - status != active iken `amount`/`due_date` KİLİTLİ (409
 *     `senet_alani_kilitli`): alanlar salt-okunur olur VE payload'a hiç
 *     konmaz — kilitli alan sunucuya gönderilip 409 yenmez.
 *   - FAZ 4 PAKET I / K-2 — TAKSİDE BAĞLI senette (`installment_id` dolu)
 *     `amount`/`due_date` HER KOŞULDA 409 `senet_taksite_bagli_alan_
 *     duzenlenemez` üretir: senet borcun kendisi değil taksidin hukuki
 *     temsilidir, tutar/vade değişimi BORÇ değişimidir ve ikisini birlikte
 *     mutasyona uğratan parçala/birleştir uçlarından geçmelidir. Ekranda bu
 *     alanlar salt-okunur, gerekçe `aria-describedby` ile bağlı.
 *   - FAZ 4 PAKET I / K-1 — TAHSİLAT isteğiyle AYNI gövdede `amount`/
 *     `due_date`/`invoice_id`/`installment_id` gönderilemez (400
 *     `senet_tahsilatinda_alan_duzenlenemez`): tahsilat tutarı senedin
 *     KAYITLI tutarıdır, tahsilat anında pazarlık edilmez. Bu yüzden
 *     `collect` işaretliyken bu alanlar payload'dan DÜŞÜRÜLÜR.
 *   - Tahsilat için banka zorunlu (400 `senet_tahsilat_banka_zorunlu`);
 *     senette bank_id varsa o kullanılır, yoksa seçim istenir.
 *     NOT: `bank_id` PUT'un `allowed` alan kümesinde DEĞİL — yalnızca
 *     tahsilat yolunda okunur, senet alanı olarak GÜNCELLENMEZ; bu yüzden
 *     tahsilat dışında gönderilmiyor.
 *   - Terminal statüde "Sil" GÖSTERİLMEZ; yerine "İptal Et" gösterilir
 *     (bkz. promissoryMath.js::noteActionAvailability).
 *   - `splitting`/`merging` (işlem yarıda kaldı) senette TÜM aksiyonlar
 *     kapatılır, "muhasebe mutabakatı gerekli" uyarısı çıkar.
 */
import { useEffect, useMemo, useState } from 'react';
import { Ban, Save, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../components/admin';
import {
  useAccountingBanks,
  useDeletePromissoryNote,
  useUpdatePromissoryNote,
} from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  NotePicker,
  TabIntro,
  noteErrorMessage,
} from './noteUiKit';
import {
  isActiveNote,
  isStuckNote,
  isValidDueDate,
  noteActionAvailability,
  normalizeDueDate,
} from './promissoryMath';

export default function NoteEditTab({ userId, notes, onRequestCancel }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const updateNote = useUpdatePromissoryNote(userId);
  const deleteNote = useDeletePromissoryNote(userId);
  const { data: banks = [], isLoading: banksLoading } = useAccountingBanks();

  const [noteId, setNoteId] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [collect, setCollect] = useState(false);
  const [paidDate, setPaidDate] = useState('');
  const [bankId, setBankId] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [deleteResult, setDeleteResult] = useState(null);

  const allNotes = useMemo(() => notes || [], [notes]);
  const note = allNotes.find((n) => n.note_id === noteId) || null;
  const active = isActiveNote(note?.status);
  const stuck = isStuckNote(note?.status);
  const availability = noteActionAvailability(note?.status);
  // K-2: takside bağlı senette tutar/vade bu uçtan ASLA değiştirilemez.
  const linkedToInstallment = !!note?.installment_id;
  // K-1: tahsilat işaretliyken de bu iki alan gönderilemez.
  // Üç kilit gerekçesi de aynı sonucu doğurur: alan salt-okunur + payload'dan düşer.
  const debtFieldsLocked = !active || linkedToInstallment || collect;

  // Seçim değiştiğinde formu senedin mevcut değerleriyle doldur.
  useEffect(() => {
    setAmount(note ? String(note.amount ?? '') : '');
    setDueDate(note ? normalizeDueDate(note.due_date) : '');
    setDescription(note?.description || '');
    setBankId(note?.bank_id || '');
    setCollect(false);
    setPaidDate('');
    setSubmitted(false);
  }, [note]);

  const amountNumber = Number(amount);
  // Kilitli alan gönderilmediği için geçersizliği submit'i bloklamamalı.
  const amountInvalid = !debtFieldsLocked && (amount === '' || !Number.isFinite(amountNumber) || amountNumber <= 0);
  const dueInvalid = !debtFieldsLocked && !!dueDate && !isValidDueDate(dueDate);
  const bankMissing = collect && !bankId;
  const canSubmit = !!note && !stuck && !amountInvalid && !dueInvalid && !bankMissing;

  /**
   * Backend'in reddettiği alan kombinasyonları payload'a HİÇ KONMAZ
   * (kullanıcıyı 400/409'a sürüklemek yerine istek baştan doğru kurulur):
   *   - `debtFieldsLocked` -> `amount`/`due_date` yok. Üç sebebi var:
   *     senet artık `active` değil (409 `senet_alani_kilitli`),
   *     senet bir takside bağlı (409 `senet_taksite_bagli_alan_duzenlenemez`),
   *     ya da aynı istekte tahsilat var (400
   *     `senet_tahsilatinda_alan_duzenlenemez`).
   *   - `invoice_id`/`installment_id` bu ekrandan zaten hiç gönderilmiyor.
   */
  const buildPayload = () => {
    const payload = { noteId: note.note_id, description };
    if (!debtFieldsLocked) {
      payload.amount = amountNumber;
      payload.due_date = normalizeDueDate(dueDate);
    }
    if (collect) {
      payload.status = 'collected';
      payload.bank_id = bankId;
      if (paidDate) payload.paid_date = paidDate;
    }
    return payload;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    if (collect) {
      const ok = await confirm({
        title: t('accounting.notes.edit.collect_confirm_title'),
        description: `${t('accounting.notes.edit.collect_confirm_description')} ${t('accounting.notes.irreversible')}`,
        variant: 'warning',
        confirmLabel: t('accounting.notes.confirm_continue'),
      });
      if (!ok) return;
    }

    try {
      await updateNote.mutateAsync(buildPayload());
      toast.success(t('accounting.notes.edit.success'));
      setCollect(false);
      setSubmitted(false);
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  const onDelete = async () => {
    if (!note || !availability.canDelete) return;
    const ok = await confirm({
      title: t('accounting.notes.edit.delete_confirm_title'),
      description: `${t('accounting.notes.edit.delete_confirm_description')} ${t('accounting.notes.irreversible')}`,
      variant: 'danger',
      confirmLabel: t('accounting.notes.confirm_continue'),
    });
    if (!ok) return;
    try {
      const res = await deleteNote.mutateAsync(note.note_id);
      toast.success(t('accounting.notes.edit.delete_success'));
      setNoteId('');
      setDeleteResult(res || {});
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  const busy = updateNote.isPending || deleteNote.isPending;

  // Kilit gerekçesi kullanıcıya AÇIKÇA yazılır (aria-describedby ile alana
  // bağlı): "neden değiştiremiyorum" sorusu ekranda cevaplanır.
  const lockHintKey = collect
    ? 'accounting.notes.edit.collect_locked_hint'
    : linkedToInstallment
      ? 'accounting.notes.edit.installment_locked_hint'
      : 'accounting.notes.edit.field_locked_hint';

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro title={t('accounting.notes.edit.title')} description={t('accounting.notes.edit.description')} />

      <NotePicker
        id="note-edit-note"
        testId="note-edit-select"
        notes={allNotes}
        value={noteId}
        onChange={(v) => { setNoteId(v); setDeleteResult(null); }}
        label={t('accounting.notes.edit.note_label')}
        required
        placeholder={t('accounting.notes.select_placeholder')}
        emptyText={t('accounting.promissory_notes.empty_description')}
        error={submitted && !noteId ? t('accounting.notes.note_required') : ''}
        lang={lang}
        t={t}
      />

      {note && stuck && (
        <InlineAlert variant="danger" title={t('accounting.notes.reconcile_title')} testId="note-edit-stuck-alert">
          {t('accounting.notes.reconcile_desc')}
        </InlineAlert>
      )}

      {note && !active && !stuck && (
        <InlineAlert variant="info" title={t('accounting.notes.edit.locked_title')} testId="note-edit-locked-alert">
          {t('accounting.notes.edit.locked_desc')}
        </InlineAlert>
      )}

      {/* K-2: takside bağlı senette tutar/vade değişimi BORÇ değişimidir. */}
      {note && active && !stuck && linkedToInstallment && (
        <InlineAlert
          variant="info"
          title={t('accounting.notes.edit.installment_locked_title')}
          testId="note-edit-installment-locked-alert"
        >
          {t('accounting.notes.edit.installment_locked_desc')}
        </InlineAlert>
      )}

      {/* DELETE yanıtı: silme İPTAL DEĞİL — soft-delete `status`'u `active`
          bırakır, `cancel_reason` yazmaz. Bu yüzden metin "iptal edildi"
          DEĞİL "silindi" der; faiz muafiyeti cümlesi iptal akışıyla AYNI
          kalır (etkisi gerçekten aynı), yalnızca eylem fiili ayrışır. */}
      {deleteResult && (
        <InlineAlert
          variant="info"
          title={t('accounting.notes.edit.delete_result_title')}
          testId="note-edit-delete-result"
        >
          {deleteResult.interest_exemption_lifted
            ? t('accounting.notes.edit.delete_result_exemption_lifted')
            : t('accounting.notes.edit.delete_result_no_installment')}
        </InlineAlert>
      )}

      {note && !stuck && (
        <>
          <Field
            id="note-edit-amount"
            label={t('accounting.notes.amount_label')}
            hint={debtFieldsLocked ? t(lockHintKey) : ''}
            error={submitted && amountInvalid ? t('accounting.notes.amount_invalid') : ''}
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                type="number"
                step="0.01"
                min="0"
                className={INPUT_CLASS}
                value={amount}
                readOnly={debtFieldsLocked}
                disabled={busy}
                aria-readonly={debtFieldsLocked || undefined}
                aria-invalid={submitted && amountInvalid ? true : undefined}
                aria-describedby={describedBy}
                onChange={(e) => { if (!debtFieldsLocked) setAmount(e.target.value); }}
                data-testid="note-edit-amount-input"
              />
            )}
          </Field>

          <Field
            id="note-edit-due"
            label={t('accounting.notes.due_label')}
            hint={debtFieldsLocked ? t(lockHintKey) : ''}
            error={dueInvalid ? t('accounting.notes.due_invalid') : ''}
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                type="date"
                className={INPUT_CLASS}
                value={dueDate}
                readOnly={debtFieldsLocked}
                disabled={busy}
                aria-readonly={debtFieldsLocked || undefined}
                aria-invalid={dueInvalid ? true : undefined}
                aria-describedby={describedBy}
                onChange={(e) => { if (!debtFieldsLocked) setDueDate(e.target.value); }}
                data-testid="note-edit-due-input"
              />
            )}
          </Field>

          <Field id="note-edit-description" label={t('accounting.notes.description_label')}>
            {({ id }) => (
              <input
                id={id}
                type="text"
                className={INPUT_CLASS}
                value={description}
                disabled={busy}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="note-edit-description-input"
              />
            )}
          </Field>

          {active && (
            <>
              <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-2 border-[var(--border-light)] accent-[var(--brand-primary)]"
                  checked={collect}
                  disabled={busy}
                  onChange={(e) => setCollect(e.target.checked)}
                  data-testid="note-edit-collect-toggle"
                />
                {t('accounting.notes.edit.collect_label')}
              </label>

              {collect && (
                <div className="space-y-3 rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3">
                  <InlineAlert variant="warning" testId="note-edit-collect-warning">
                    {t('accounting.notes.edit.collect_warning')}
                  </InlineAlert>

                  <Field
                    id="note-edit-bank"
                    label={t('accounting.notes.bank_label')}
                    required
                    error={submitted && bankMissing ? t('accounting.notes.edit.bank_required') : ''}
                  >
                    {({ id, describedBy }) => (
                      <select
                        id={id}
                        className={INPUT_CLASS}
                        value={bankId}
                        disabled={busy || banksLoading}
                        aria-required
                        aria-invalid={submitted && bankMissing ? true : undefined}
                        aria-describedby={describedBy}
                        onChange={(e) => setBankId(e.target.value)}
                        data-testid="note-edit-bank-select"
                      >
                        <option value="">{t('accounting.collect_form.bank_placeholder')}</option>
                        {banks.map((b) => (
                          <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
                        ))}
                      </select>
                    )}
                  </Field>

                  <Field id="note-edit-paid-date" label={t('accounting.notes.edit.paid_date_label')}>
                    {({ id }) => (
                      <input
                        id={id}
                        type="date"
                        className={INPUT_CLASS}
                        value={paidDate}
                        disabled={busy}
                        onChange={(e) => setPaidDate(e.target.value)}
                        data-testid="note-edit-paid-date-input"
                      />
                    )}
                  </Field>
                </div>
              )}
            </>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {availability.showDelete && (
              <ActionButton
                variant="neutral"
                icon={Trash2}
                onClick={onDelete}
                disabled={busy || !availability.canDelete}
                testId="note-edit-delete-btn"
              >
                {t('accounting.notes.edit.delete')}
              </ActionButton>
            )}
            {availability.showCancel && (
              <ActionButton
                variant="neutral"
                icon={Ban}
                onClick={() => onRequestCancel?.(note.note_id)}
                disabled={busy || !availability.canCancel}
                testId="note-edit-cancel-btn"
              >
                {t('accounting.notes.edit.cancel_note')}
              </ActionButton>
            )}
            <ActionButton
              type="submit"
              variant="success"
              icon={Save}
              loading={updateNote.isPending}
              disabled={busy}
              testId="note-edit-submit"
            >
              {updateNote.isPending ? t('accounting.notes.submitting') : t('accounting.notes.edit.submit')}
            </ActionButton>
          </div>

          {!availability.canCancel && availability.showCancel && (
            <p className="text-[11px] text-[var(--text-muted)] text-right" data-testid="note-edit-cancel-hint">
              {t('accounting.notes.edit.cancel_unavailable_hint')}
            </p>
          )}
        </>
      )}
    </form>
  );
}
